import React, { useEffect, useState } from 'react';
import MuiInputTextWithRemainingChars from '@shared/components/MuiInputTextWithRemainingChars';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@mui/material';
import { Page } from '../../layout';
import PageInstructions from '@shared/components/PageInstructions';
import { Breadcrumb } from '@shared/components/breadcrumb/Breadcrumb';
import GET_CHECKPOINT_REAPPRAISAL_RATIONALE_REVIEW from '../queries/checkpointReappraisalRationaleReviewQuery';
import UPDATE_CHECKPOINT_REAPPRAISAL_RATIONALE_REVIEW from '../queries/updateCheckpointReappraisalRationaleReviewMutation';
import ApolloError from '@shared/components/ApolloError';
import { useQuery, useMutation } from '@apollo/client';
import REAPPRAISAL_RATIONALE_STATUS_TYPE from '../constants/reappraisalRationaleStatusTypes';
import ConfirmDialogWithButton from '../../shared/components/confirmDialog/ConfirmDialogWithButton';
import { newLine } from '../../../constants/textConstants';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import ErrorMessage from '@shared/components/ErrorMessage';
import ReappraisalRationalReviewInputValidator from '../validators/reappraisalRationalReviewInputValidator';
import {
  COMMENT_MAX_LENGTH,
  REAPPRAISAL_RATIONALE_REVIEW_DISPLAY_NAMES
} from '../constants/reappraisalRationaleReviewConstants';

const ReappraisalRationaleReviewPage = () => {
  const { checkPointId } = useParams();
  const checkPointIdValue = parseInt(checkPointId);
  const navigate = useNavigate();

  const [returnButtonClicked, setReturnButtonClicked] = useState(false);
  const [commentInput, setCommentInput] = useState('');

  const {
    data: reappraisalRationalReviewData,
    loading: reappraisalRationalReviewLoading,
    error: reappraisalRationalReviewError
  } = useQuery(GET_CHECKPOINT_REAPPRAISAL_RATIONALE_REVIEW, {
    variables: {
      checkPointId: checkPointIdValue
    }
  });

  const [updateReappraisalRationaleReview, { error: updateError }] = useMutation(
    UPDATE_CHECKPOINT_REAPPRAISAL_RATIONALE_REVIEW
  );

  useEffect(() => {
    if (!reappraisalRationalReviewData?.checkPointReappraisalRationaleReview?.Comment) {
      return;
    }

    setCommentInput(reappraisalRationalReviewData?.checkPointReappraisalRationaleReview?.Comment);
  }, [reappraisalRationalReviewData]);

  const review = reappraisalRationalReviewData?.checkPointReappraisalRationaleReview || {};

  const breadcrumbPathItems = [{ name: pageTitle, url: '' }];
  const annotationItems = [
    review.MddapOrgUnit?.MddapOrganization?.Name,
    review.MddapOrgUnit?.Name,
    review.AppraisalProgramYear
  ];

  const isCommentInputValid = !ReappraisalRationalReviewInputValidator.validateInput({
    comment: commentInput
  }).error;

  const shouldDisplayCommentInputWarningMessage = returnButtonClicked && !isCommentInputValid;

  const handleOnCommentChange = e => {
    setCommentInput(e.target.value);
  };

  const handleDownloadButtonClick = () => {
    window.open(review.CheckPointDocumentFileUrl, '_blank');
  };

  const handleReturnButtonClick = () => {
    setReturnButtonClicked(true);
  };

  const handleOnConfirm = async status => {
    await updateReappraisalRationaleReview({
      variables: {
        review: {
          Id: review.Id,
          CheckPointId: checkPointIdValue,
          Status: status,
          Comment: status === REAPPRAISAL_RATIONALE_STATUS_TYPE.RETURNED ? commentInput : null
        }
      }
    });

    navigate('/data-reports', {
      state: {
        successMessage:
          status === REAPPRAISAL_RATIONALE_STATUS_TYPE.APPROVED
            ? approvedSuccessMessage
            : returnedSuccessMessage
      }
    });
  };

  if (
    reappraisalRationalReviewData &&
    reappraisalRationalReviewData.checkPointReappraisalRationaleReview === null
  ) {
    return <Navigate to='/permissions' />;
  }

  if (reappraisalRationalReviewError || updateError) {
    return <ApolloError />;
  }

  return (
    <Page title={pageTitle} className='fda-review-page'>
      <Breadcrumb
        pathItems={breadcrumbPathItems}
        annotationItems={annotationItems}
        loading={reappraisalRationalReviewLoading}>
        <div className='actions'>
          <Button
            className='primary-button sm'
            onClick={handleDownloadButtonClick}
            disabled={!reappraisalRationalReviewData}>
            Download
          </Button>
          {review.Status === REAPPRAISAL_RATIONALE_STATUS_TYPE.PENDING_REVIEW && (
            <ConfirmDialogWithButton
              openModalBtnText='Approve'
              handleOnConfirm={() => handleOnConfirm(REAPPRAISAL_RATIONALE_STATUS_TYPE.APPROVED)}
              message={approveModalTitle}
            />
          )}
        </div>
      </Breadcrumb>
      <section className='page-instructions'>
        <PageInstructions title={pageTitle} description={description} />
      </section>
      <div className='input-area'>
        <div className='label bold'>Status:</div>
        <SkeletonWrapper show={reappraisalRationalReviewLoading}>
          <div className='value'>
            {REAPPRAISAL_RATIONALE_REVIEW_DISPLAY_NAMES.get(review.Status)}
          </div>
        </SkeletonWrapper>
      </div>
      <div className='input-area'>
        <div className='label bold'>Reviewer:</div>
        <SkeletonWrapper show={reappraisalRationalReviewLoading}>
          <div className='value'> {review.ReviewerName || 'TBD'}</div>
        </SkeletonWrapper>
      </div>
      {(review.Status === REAPPRAISAL_RATIONALE_STATUS_TYPE.RETURNED ||
        review.Status === REAPPRAISAL_RATIONALE_STATUS_TYPE.PENDING_REVIEW) && (
        <div className='input-container'>
          <div className='input-area'>
            <div className='label bold'>Comments:</div>
            <MuiInputTextWithRemainingChars
              className='value'
              error={shouldDisplayCommentInputWarningMessage}
              disableUnderline
              onChange={handleOnCommentChange}
              multiline={true}
              rows={5}
              value={commentInput}
              disabled={
                reappraisalRationalReviewLoading ||
                review.Status !== REAPPRAISAL_RATIONALE_STATUS_TYPE.PENDING_REVIEW
              }
              maxLength={COMMENT_MAX_LENGTH}
              hideRemainingChars={false}
            />
          </div>
          {review.Status === REAPPRAISAL_RATIONALE_STATUS_TYPE.PENDING_REVIEW && (
            <div className='return-action'>
              <ConfirmDialogWithButton
                openModalBtnText='Return'
                openModalBtnType='secondary-button'
                openModalPreCondition={() => {
                  return isCommentInputValid;
                }}
                openModalBtnHandleOnClick={handleReturnButtonClick}
                handleOnConfirm={() => handleOnConfirm(REAPPRAISAL_RATIONALE_STATUS_TYPE.RETURNED)}
                message={returnModalTitle}
                disabled={
                  reappraisalRationalReviewLoading || shouldDisplayCommentInputWarningMessage
                }
              />
              {shouldDisplayCommentInputWarningMessage && (
                <ErrorMessage message={commentInputWarningMessage} className='error-message' />
              )}
            </div>
          )}
        </div>
      )}
    </Page>
  );
};

const pageTitle = 'Reappraisal Rationale Review';

const description =
  'To review the reappraisal rationale, click the “Download” button to receive a copy of the file.' +
  ' After reviewing the reappraisal rationale, select either the “Approve” button to inform the Lead ' +
  'Appraiser that they may proceed with the appraisal as planned, or select the “Return” button to ' +
  'inform the Lead Appraiser of change requests to the appraisal criteria (in the open text box). ' +
  'The reappraisal rationale will be automatically approved if no action is taken after 3' +
  ' business days from submission.';

const commentInputWarningMessage =
  'Comment field is required.' + newLine + 'Must be a minimum of 25 characters.';

const approveModalTitle = 'Are you sure you want to approve?';
const returnModalTitle = 'Are you sure you want to return the Reappraisal Rationale to the ATL?';

const approvedSuccessMessage = 'Reappraisal Rationale has been successfully Approved.';
const returnedSuccessMessage = 'Reappraisal Rationale has been successfully Returned.';

export default ReappraisalRationaleReviewPage;
